<template>
  <div>
    <PageAllow controller="FoodBehavior" action="index" />
    <Breadcrumbs :items="items" />
    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="8" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherKeyword
                ref="name"
                v-model="options.keyword"
                @text="keyword = $event"
                :label="$t('CookingMethods.cookingMethodsKeyword')"
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="4" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-spacer />
              <v-btn
                v-if="$permission.permissionAllow('Configs', 'add')"
                small
                color="primary"
                @click.stop="editDialog = true"
                class="ms-1"
              >
                <v-icon x-small class="pe-1">fa-plus</v-icon>
                {{ $t("CRUD.newInfo", [$t("CookingMethods.ObjectName")]) }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="cookingMethods"
            :options.sync="options"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            :loading="loading"
            multi-sort
            class="elevation-2 px-4 pt-1"
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.actions="{ header }">
              {{ header.text }}
              <v-icon x-small>fa-sliders-h</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip
                top
                v-if="$permission.permissionAllow('FoodBehavior', 'edit')"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="px-1 me-1"
                    @click.stop="
                      currentItem = item;
                      editDialog = true;
                    "
                    >fa-pencil-alt
                  </v-icon>
                </template>
                <span>{{ $t("CRUD.editInfo", [$t("CookingMethods.ObjectName")]) }}</span>
              </v-tooltip>
              <v-tooltip
                top
                v-if="$permission.permissionAllow('FoodBehavior', 'delete')"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="danger"
                    class="px-1 me-1"
                    @click.stop="
                      currentItem = item;
                      deleteDialog = true;
                    "
                    >fa-trash-alt
                  </v-icon>
                </template>
                <span>{{
                  $t("CRUD.deleteInfo", [$t("CookingMethods.ObjectName")])
                }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>

    <v-dialog persistent v-model="editDialog" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ $t("CookingMethods.title") }}
          </span>
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="editDialog = false">
                fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("CookingMethods.title")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-form>
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('CookingMethods.Fields.code')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.behavior_code"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'behavior_code')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("CookingMethods.Fields.code") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('CookingMethods.Fields.name')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.behavior_name"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'behavior_name')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("CookingMethods.Fields.name") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-form>
            </ValidationObserver>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="editDialog = false">
            {{ $t("$vuetify.close") }}
          </v-btn>
          <v-btn color="accent" @click="save()">
            {{ $t("CRUD.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogDelete
      v-model="deleteDialog"
      :label="$t('CookingMethods.ObjectName')"
      @confirm="
        del();
        deleteDialog = false;
      "
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import DialogDelete from "@/components/plugins/DialogDelete";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import CookingMethods from "@/api/cooking_methods";

const defaultOptions = {
  keyword: "",
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    DialogDelete,
    ValidationObserver,
    ValidationProvider,
  },
  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.dishManagement"),
        disabled: false,
        href: "/dishes",
      },
      {
        text: i18n.t("Nav.cookingMethods"),
        disabled: true,
        href: "/dishes/cooking-methods/",
      },
    ],

    headers: () => [
      {
        text: i18n.t("CookingMethods.Fields.code"),
        align: "center",
        sortable: false,
        value: "behavior_code",
      },
      {
        text: i18n.t("CookingMethods.Fields.name"),
        align: "start",
        sortable: false,
        value: "behavior_name",
        width: 380,
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "start",
        class: "text-no-wrap",
        sortable: false,
        value: "actions",
        width: 380,
      },
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    keyword: "",
    paging: {},
    cookingMethods: [],
    options: JSON.parse(JSON.stringify(defaultOptions)),
    currentItem: {},
    serverErrors: [],
    editDialog: false,
    deleteDialog: false,
  }),

  mounted() {
      this.getCookingMethodsFromAPI();
  },

  methods: {
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({},this.options, {
        keyword: this.keyword,
      });
    },
    getCookingMethodsFromAPI() {
      let options = JSON.parse(JSON.stringify(this.options));
      CookingMethods.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.cookingMethods = response.data.food_behavior;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    save() {
      this.serverErrors = [];
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            this.$store.dispatch("fullscreenLoading");
            CookingMethods.save(this.currentItem.id, this.currentItem)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.currentItem);
                    this.getCookingMethodsFromAPI();
                    this.editDialog = false;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                    this.$store.dispatch("fullscreenLoadingStop");
                  }
                }.bind(this)
              )
              .catch(
                function () {
                  this.editDialog = false;
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          }
        }.bind(this)
      );
    },
    del() {
      CookingMethods.del(this.currentItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.currentItem = {};
            this.getCookingMethodsFromAPI();
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getCookingMethodsFromAPI();
      },
    },
    editDialog: {
      handler() {
        if (!this.editDialog) {
          this.$refs.observer.reset();
          this.serverErrors = [];
          this.currentItem = {};
          this.$store.dispatch("fullscreenLoadingStop");
        }
      },
    },
  },
};
</script>